import React from 'react';
import { EndorsementPublic, EndorsementWithProviders } from '../types/endorsement';
import { ProviderPublic as ProviderToEndorse } from '../types/provider';
import NotAvailable from './Common/NotAvailable';

type EndorsementItem = EndorsementPublic | ProviderToEndorse | EndorsementWithProviders;

function isProviderToEndorse(item: EndorsementItem): item is ProviderToEndorse {
  return 'business_name' in item && !('endorser_id' in item);
}

function isSentEndorsement(item: EndorsementItem): item is EndorsementWithProviders {
  return 'endorsed_name' in item;
}

const StarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8 1.33334L10.06 5.50668L14.6667 6.18001L11.3333 9.42668L12.12 14.0133L8 11.8467L3.88 14.0133L4.66667 9.42668L1.33334 6.18001L5.94 5.50668L8 1.33334Z"
      fill="#c6a664"
      stroke="#c6a664"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Up to two initials from a name, e.g. "Glow Aesthetics" -> "GA". */
export const getInitials = (name?: string | null): string =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

interface AvatarProps {
  imageUrl?: string | null;
  name: string;
  textClassName?: string;
}

/**
 * Fills a round avatar frame with the person's photo, or with their initials drawn
 * locally when there is no photo (no third-party avatar service sees the name).
 */
export const Avatar: React.FC<AvatarProps> = ({ imageUrl, name, textClassName = 'text-2xl' }) =>
  imageUrl ? (
    <img src={imageUrl} alt={name} className="w-full h-full object-cover" />
  ) : (
    <div
      role="img"
      aria-label={name}
      className="w-full h-full bg-gray-300 flex items-center justify-center"
    >
      <span className={`font-['Open_Sans'] font-semibold text-gray-600 ${textClassName}`}>
        {getInitials(name)}
      </span>
    </div>
  );

interface EndorsementCardProps {
  item: EndorsementItem;
  onAccept?: (id: number) => void;
  onReject?: (id: number) => void;
  onEndorse?: (id: number) => void;
  isEndorsing?: boolean;
  tab: 'received' | 'sent' | 'pending' | 'get-endorsed';
}

export const EndorsementCard: React.FC<EndorsementCardProps> = ({
  item,
  onAccept,
  onReject,
  onEndorse,
  isEndorsing,
  tab,
}) => {
  if (tab === 'get-endorsed' && isProviderToEndorse(item)) {
    return (
      <div className="bg-white rounded-lg p-6 text-center shadow-sm">
        <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-4">
          <Avatar imageUrl={item.profile_image_url} name={item.business_name} />
        </div>
        <h3 className="font-['Ovo'] text-base text-[#1f1f1f] uppercase mb-1">
          {item.business_name}
        </h3>
        <p className="font-['Open_Sans'] text-sm text-[#767472] mb-2">
          {item.specialties?.[0] || <NotAvailable />}
        </p>
        <div className="flex items-center justify-center gap-1 mb-4">
          <StarIcon />
          <span className="font-['Open_Sans'] text-sm font-semibold text-[#1f1f1f]">
            {item.clout_score.toFixed(1)} CLOUT SCORE
          </span>
        </div>
        <button
          onClick={() => onEndorse?.(item.id)}
          disabled={isEndorsing}
          className="w-full px-4 py-2 bg-white text-[#1f1f1f] border border-[#c6a664] font-['Ovo'] text-sm uppercase hover:bg-[#c6a664] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isEndorsing ? 'ENDORSING...' : 'ENDORSE'}
        </button>
      </div>
    );
  }

  if (tab === 'pending' && !isProviderToEndorse(item) && !isSentEndorsement(item)) {
    return (
      <div key={item.id} className="bg-white rounded-lg p-6 shadow-sm">
        <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-4">
          <Avatar imageUrl={item.endorser_profile_image_url} name={item.endorser_name} />
        </div>
        <h3 className="font-['Ovo'] text-base text-[#1f1f1f] uppercase mb-1 text-center">
          {item.endorser_business_name}
        </h3>
        <p className="font-['Open_Sans'] text-sm text-[#767472] mb-2 text-center">
          {item.endorser_name}
        </p>
        <div className="flex items-center justify-center gap-1 mb-4">
          <StarIcon />
          <span className="font-['Open_Sans'] text-sm font-semibold text-[#1f1f1f]">
            {item.endorser_clout_score.toFixed(1)} CLOUT SCORE
          </span>
        </div>
        {item.message && (
          <p className="font-['Open_Sans'] text-xs text-[#767472] mb-4 text-center italic">
            "{item.message}"
          </p>
        )}
        <div className="flex gap-2">
          <button
            onClick={() => onAccept?.(item.id)}
            className="flex-1 px-3 py-2 bg-[#c6a664] text-white font-['Ovo'] text-xs uppercase hover:bg-[#b39754] transition-colors"
          >
            ACCEPT
          </button>
          <button
            onClick={() => onReject?.(item.id)}
            className="flex-1 px-3 py-2 bg-white text-[#1f1f1f] border border-[#1f1f1f] font-['Ovo'] text-xs uppercase hover:bg-[#1f1f1f] hover:text-white transition-colors"
          >
            REJECT
          </button>
        </div>
      </div>
    );
  }

  if (tab === 'sent' && isSentEndorsement(item)) {
    return (
      <div key={item.id} className="bg-white rounded-lg p-6 shadow-sm">
        <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-4">
          <Avatar imageUrl={item.endorsed_profile_image_url} name={item.endorsed_name} />
        </div>
        <h3 className="font-['Ovo'] text-base text-[#1f1f1f] uppercase mb-1 text-center">
          {item.endorsed_business_name}
        </h3>
        <p className="font-['Open_Sans'] text-sm text-[#767472] mb-2 text-center">
          {item.endorsed_name}
        </p>
        <div className="flex items-center justify-center gap-1 mb-4">
          <StarIcon />
          <span className="font-['Open_Sans'] text-sm font-semibold text-[#1f1f1f]">Endorsed</span>
        </div>
      </div>
    );
  }

  if (tab === 'received' && !isProviderToEndorse(item) && !isSentEndorsement(item)) {
    return (
      <div key={item.id} className="bg-white rounded-lg p-6 shadow-sm">
        <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-4">
          <Avatar imageUrl={item.endorser_profile_image_url} name={item.endorser_name} />
        </div>
        <h3 className="font-['Ovo'] text-base text-[#1f1f1f] uppercase mb-1 text-center">
          {item.endorser_business_name}
        </h3>
        <p className="font-['Open_Sans'] text-sm text-[#767472] mb-2 text-center">
          {item.endorser_name}
        </p>
        <div className="flex items-center justify-center gap-1 mb-4">
          <StarIcon />
          <span className="font-['Open_Sans'] text-sm font-semibold text-[#1f1f1f]">
            {item.endorser_clout_score?.toFixed(1)} CLOUT SCORE
          </span>
        </div>
        {item.is_from_top_scorer && (
          <div className="bg-[#c6a664] text-white px-2 py-1 rounded-full text-xs font-['Open_Sans'] font-semibold text-center mb-2">
            TOP SCORER
          </div>
        )}
        {item.rating && (
          <div className="flex items-center justify-center gap-1 mb-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <svg
                key={star}
                width="12"
                height="12"
                viewBox="0 0 16 16"
                fill={star <= (item.rating || 0) ? '#c6a664' : 'none'}
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 1.33334L10.06 5.50668L14.6667 6.18001L11.3333 9.42668L12.12 14.0133L8 11.8467L3.88 14.0133L4.66667 9.42668L1.33334 6.18001L5.94 5.50668L8 1.33334Z"
                  stroke="#c6a664"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ))}
          </div>
        )}
        {item.message && (
          <p className="font-['Open_Sans'] text-xs text-[#767472] text-center italic">
            "{item.message}"
          </p>
        )}
      </div>
    );
  }

  return null;
};
